import { isConfigSmava, isTenantSmava } from 'utils/url'
import { useEffect, useState } from 'react'
import { isInABTestContactPageVariantB } from 'utils/tracking/sitespect/campaigns'

import countryDeOther from '../../../texts/common/lists/country_de_other'
import countryShort from '../../../texts/common/lists/country_short'
import countryLong from '../../../texts/common/lists/country_long'
import occupation from '../../../texts/common/lists/occupation'
import secondaryEmploymentType from '../../../texts/common/lists/secondary_employment_type'
import position from '../../../texts/common/lists/position'
import sector from '../../../texts/common/lists/sector'
import bankAccountType from '../../../texts/common/lists/bank_account_type'
import commonPartial from '../../../texts/common/partials/common'
import debtorPartial from '../../../texts/common/partials/debtor'
import pension from '../../../texts/common/lists/pension'
import headline from '../../../texts/common/copy/headline'
import clue from '../../../texts/common/copy/clue'
import introduction from '../../../texts/common/copy/introduction'
import relationship from '../../../texts/common/lists/relationship'
import countEnum from '../../../texts/common/lists/count_enum'
import yesNoEnum from '../../../texts/common/lists/yes_no_enum'
import healthInsuranceEnum from '../../../texts/common/lists/health_insurance_enum'
import debtorsCount from '../../../texts/common/lists/debtors_count'
import { loanSelections } from '../../../texts/smava/loan_selector'
import { useRootStore } from './useRootStore'

export function useText() {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [textContent, setTextContent] = useState<any>({
    common: {},
    copy: {},
    debtors: {},
    lists: {}
  })

  const {
    rootStore: {
      navigation: { tenantContext },
      traversal: {
        data: {
          system: { refId }
        }
      }
    }
  } = useRootStore()
  const isSmava = isTenantSmava()
  const isSmavaConfig = isConfigSmava()

  useEffect(() => {
    if (!isSmava) {
      return
    }

    const common = { ...commonPartial }
    common.loanPayout.label = 'Auszahlungstermin'
    common.purpose.options = [
      { label: 'Freie Verwendung', value: 'OTHER' },
      { label: 'Auto / Motorrad', value: 'PRE_OWNED_CAR' },
      { label: 'Umschuldung', value: 'REFINANCING' },
      { label: 'Wohnen / Modernisierung', value: 'RENOVATION' },
      {
        label: 'Bau-/ Immobilienfinanzierung',
        value: 'REAL_ESTATE_FINANCING'
      }
    ]

    common.vehicle.initialPayment = {
      label: 'Anzahlung',
      placeholder: 'z.B. 5.000',
      tooltip:
        'Falls Sie einen Teil des Kaufpreises sofort anzahlen möchten, können Sie sich bessere Konditionen bei unseren Partnerbanken sichern.'
    }

    const clonedHeadline = { ...headline }
    clonedHeadline.payoutAccount = 'Auszahlungskonto'

    const clonedDebatorsCount = [...debtorsCount]
    clonedDebatorsCount[0].label = isSmavaConfig ? 'eine Person' : '1 Person'
    clonedDebatorsCount[1].label = isSmavaConfig ? 'zwei Person' : '2 Person'

    const clonedDebatorPartial = { ...debtorPartial }
    clonedDebatorPartial.contact.phoneNumber.placeholder = 'z.B. 0170123456789'
    clonedDebatorPartial.contact.phoneNumber.label = 'Mobilfunknummer'
    clonedDebatorPartial.contact.phoneNumber.errorMap.missing =
      'Mobilnummer ist ein Pflichtfeld.'
    clonedDebatorPartial.contact.phoneNumber.errorMap.invalid =
      'Bitte korrigieren Sie die Mobilfunknummer.'
    clonedDebatorPartial.contact.phoneNumber.errorMap.wrong_format =
      'Bitte geben Sie eine gültige Mobilfunknummer an.'

    clonedDebatorPartial.contact.email.errorMap.missing =
      'E-Mail ist ein Pflichtfeld.'
    clonedDebatorPartial.contact.email.errorMap.wrong_format =
      'E-Mail hat nicht das richtige Format für eine E-Mail-Adresse.'

    clonedDebatorPartial.personal.firstname.label = 'Vorname(n)'
    clonedDebatorPartial.personal.firstname.errorMap.missing =
      'Vorname(n) ist ein Pflichtfeld.'

    clonedDebatorPartial.personal.birthday.errorMap.missing =
      'Bitte geben Sie hier ein gültiges Datum ein.'

    clonedDebatorPartial.personal.gender.errorMap.missing =
      'Anrede ist ein Pflichtfeld.'

    clonedDebatorPartial.personal.lastname.errorMap.missing =
      'Nachname ist ein Pflichtfeld.'

    clonedDebatorPartial.activeLoans.isDebtConversionWanted.tooltip = ''

    common.purpose.label = 'Verwendung'

    common.amount.label = 'Nettokreditbetrag'

    common.manuallyAdaptedAmount.tooltip = ''

    if (isSmavaConfig) {
      common.payoutAccount.type.label =
        'Bitte wählen Sie, ob Sie Kontonummer oder IBAN angeben möchten.'
    }

    if (isSmava) {
      clonedDebatorPartial.currentAddress.street.label = 'Straße'
      clonedDebatorPartial.currentAddress.street.placeholder =
        'z.B. Musterstraße'

      clonedDebatorPartial.previousAddress.street.label = 'Straße'
      clonedDebatorPartial.previousAddress.street.placeholder =
        'z.B. Musterstraße'
    }

    clonedDebatorPartial.currentAddress.houseNumber.tooltip = ''

    const debtors = {
      count: {
        tooltip: '',
        enums: countEnum,
        options: clonedDebatorsCount
      },
      isCommonHousehold: {
        label: 'Gemeinsame Haushaltsführung?',
        tooltip: '',
        enums: yesNoEnum,
        placeholder: 'Gemeinsame Haushaltsführung'
      },
      isSameAddress: {
        label: 'Gleiche Adresse?',
        tooltip: '',
        placeholder: 'gleiche Adresse für KN 2',
        enums: yesNoEnum
      },
      isSameContactInformation: {
        label: 'Gleiche Kontaktdaten wie 1. Kreditnehmer?',
        tooltip: '',
        placeholder: 'gleiche Kontaktdaten für KN 2'
      },
      primary: clonedDebatorPartial,
      relationship: {
        label: 'Verhältnis der Kreditnehmer',
        tooltip: '',
        placeholder: 'Verhältnis der Kreditnehmer',
        options: relationship
      },
      secondary: clonedDebatorPartial
    }

    const lists = {
      countries: countryShort,
      countriesShort: countryDeOther,
      nationalities: countryLong,
      occupations: occupation,
      pension,
      positions: position,
      secondaryEmploymentType,
      sectors: sector,
      bankInfos: bankAccountType,
      healthInsurance: healthInsuranceEnum,
      loanSelections
    }

    const clonedClue = { ...clue }
    clonedClue.occupation.profession =
      'Die Angabe dient der Bank zum Abgleich mit Ihrem Einkommensnachweis. Ihr Arbeitgeber wird über Ihre Kreditabsichten zu keinem Zeitpunkt von smava informiert.'

    const copy = {
      headline: clonedHeadline,
      introduction,
      clue: clonedClue,
      dac: 'Ich bin ausdrücklich einverstanden, dass smava meine Kontoauszüge (inkl. enthaltener besonderer Kategorien personenbezogener Daten) erhebt und zur Kreditprüfung an mögliche Kreditgeber weiterleitet. Ich kann meine Einwilligung jederzeit widerrufen.'
    }

    if (refId || tenantContext === 'external') {
      const currentOptions = [...common.purpose.options]
      const updatedOptions = currentOptions.filter(
        ({ value }: { value: string }) =>
          !['REAL_ESTATE_FINANCING'].includes(value)
      )
      common.purpose.options = updatedOptions

      const currentLoanSelections = [...lists.loanSelections]
      const updatedLoanSelections = currentLoanSelections.filter(
        (selection: { isMortgage: boolean }) => {
          return !selection.isMortgage
        }
      )
      lists.loanSelections = updatedLoanSelections
    }

    setTextContent({ common, debtors, copy, lists })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantContext])

  useEffect(() => {
    if (isSmava) {
      return
    }

    const clonedDebatorPartial = { ...debtorPartial }

    const debtors = {
      count: {
        label: 'Anzahl Kreditnehmer',
        tooltip: '',
        placeholder: 'Anzahl Kreditnehmer',
        enums: countEnum,
        options: debtorsCount
      },
      isCommonHousehold: {
        label: 'Gemeinsame Haushaltsführung?',
        tooltip: '',
        enums: yesNoEnum,
        placeholder: 'Gemeinsame Haushaltsführung'
      },
      isSameAddress: {
        label: 'Gleiche Adresse?',
        tooltip: '',
        placeholder: 'gleiche Adresse für KN 2',
        enums: yesNoEnum
      },
      isSameContactInformation: {
        label: 'Gleiche Kontaktdaten wie 1. Kreditnehmer?',
        tooltip: '',
        placeholder: 'gleiche Kontaktdaten für KN 2'
      },
      primary: debtorPartial,
      relationship: {
        label: 'Verhältnis der Kreditnehmer',
        tooltip: '',
        placeholder: 'Verhältnis der Kreditnehmer',
        options: relationship
      },
      secondary: debtorPartial
    }

    if (isInABTestContactPageVariantB()) {
      clonedDebatorPartial.contact.email.tooltip =
        'An diese E-Mail-Adresse werden Ihre Angebote verschickt.'
    }

    const lists = {
      countries: countryShort,
      countriesShort: countryDeOther,
      nationalities: countryLong,
      occupations: occupation,
      pension,
      positions: position,
      secondaryEmploymentType,
      sectors: sector,
      bankInfos: bankAccountType,
      healthInsurance: healthInsuranceEnum,
      loanSelections: []
    }

    const copy = {
      headline,
      introduction,
      clue,
      dac: 'Ich bin ausdrücklich einverstanden, dass FINANZCHECK meine Kontoauszüge (inkl. enthaltener besonderer Kategorien personenbezogener Daten) erhebt und zur Kreditprüfung an mögliche Kreditgeber weiterleitet. Ich kann meine Einwilligung jederzeit widerrufen.'
    }

    setTextContent({ common: { ...commonPartial }, debtors, copy, lists })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return { text: textContent }
}
